import { Controller } from "@hotwired/stimulus"

// Mounted on `.billing-address-component` in _card.html.haml. A successful save replaces this
// controller's own element (`this.element.outerHTML = data.html`, another render of that same
// partial), which Stimulus sees as a disconnect/connect - so there is no manual re-init here.
// `data.html` comes from billing_address_component_to_string, a server render of that same
// partial on our own origin with HAML's default auto-escaping on every interpolated field (see
// _current.html.haml's `= h billing_address.street`) - not attacker-controlled markup, so the
// outerHTML write is safe.
//
// Every handler is wired declaratively with `data-action`, so connect()/disconnect() have nothing
// to bind or unbind.
//
// This form has no client-side field validation and so does not use the shared `validation`
// controller: every error shown here comes from a 422 body and is repainted on each submit.
//
// The request body is form-urlencoded, matching a plain form post, and Rails' method override
// handles the create/update split from the form's own hidden `_method`. The X-Requested-With and
// Accept headers are required for the `respond_to` to take its `format.json` branch rather than
// answering with a redirect.
//
// Two branches are dead on every current mount: deleteAddress(), since no view renders a
// `.delete-billing-address` trigger, and toggleUpdate()'s second `.toggle()`, since no view
// renders "#new-user-billing-address-form". toggleUpdate()'s first branch is live (users/show and
// teams/show mount this component with no `opened:`, so "Add billing address" renders as a real
// click target) but has no covering spec anywhere in the suite - left on jQuery's `.toggle()`
// rather than converting a path nothing can prove.
export default class extends Controller {
  static get targets() {
    return ["newOrganizationAddressForm"];
  }

  static get values() {
    return {
      form: String,
      model: String
    };
  }

  async submit(event) {
    event.preventDefault();
    const form = event.target;
    const loader = form.querySelector(".loader");
    if (loader) { loader.classList.remove("hidden"); }
    this.disableSubmit(form);

    const formId = this.formValue;
    const formModel = this.modelValue;

    // Unscoped on purpose: clears every .help-block/.has-error on the page, not just this form's.
    document.querySelectorAll(".help-block").forEach((el) => el.remove());
    document.querySelectorAll(".form-group.has-error").forEach((el) => el.classList.remove("has-error"));

    let response;
    try {
      response = await fetch(form.action, {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Accept": "application/json, text/plain, */*"
        },
        body: new URLSearchParams(new FormData(form))
      });
    } catch (e) {
      alert("Something went wrong. Please try again.");
      if (loader) { loader.classList.add("hidden"); }
      this.enableSubmit(form);
      return;
    }

    if (response.ok) {
      const data = await response.json();
      this.addOrReplaceHiddenInput(formId, formModel, data.id);

      // S1, accepted: server-rendered HTML from our own origin. Subdomain::Accounts::
      // BillingAddressesController#create and #update both return
      // `billing_address_component_to_string`, a render_to_string of
      // subdomain/accounts/components/billing_addresses/_card. Read that partial and the three it
      // renders (_current, _form, _delete_modal): no `raw`/`html_safe`/`!=` in any of them, and
      // _current escapes the address fields it prints with an explicit `= h`. _card's `:javascript`
      // block is gated on `opened`, which neither of those calls passes, so it is not in this body
      // at all -- and outerHTML does not execute scripts even when it is. See the file header for
      // why replacing this controller's own element needs no manual re-init.
      this.element.outerHTML = data.html;
      return;
    }

    if (response.status === 422) {
      const data = await response.json();
      if (data.errors) {
        this.paintErrors(data.errors);
      } else {
        alert("Something went wrong. Please try again.");
      }
    } else {
      alert("Something went wrong. Please try again.");
    }

    if (loader) { loader.classList.add("hidden"); }
    this.enableSubmit(form);
  }

  toggleReplace(event) {
    event.preventDefault();
    // Same set jQuery's original `.find(".billing-address-widget").siblings(".billing-address-widget")`
    // produced: the two `.billing-address-widget` elements are siblings of each other, so the
    // union of "each one's siblings matching the selector" is just both of them.
    const panel = event.currentTarget.closest(".panel");
    panel.querySelectorAll(".billing-address-widget").forEach((el) => {
      el.style.display = getComputedStyle(el).display === "none" ? "" : "none";
    });
    event.currentTarget.closest(".actions")?.classList.remove("open"); // Close dropdown
  }

  toggleUpdate(event) {
    event.preventDefault();
    if (this.hasNewOrganizationAddressFormTarget) { $(this.newOrganizationAddressFormTarget).toggle(); }
    $("#new-user-billing-address-form").toggle();
  }

  deleteAddress(event) {
    event.preventDefault();
    const $deleteModal = $("#billing-address-delete-modal");
    $deleteModal.modal("show");
    $deleteModal.find(".modal-footer .btn[data-method='delete']").attr("href", event.target.getAttribute("href"));
  }

  toggleState(event) {
    const stateWrapper = document.getElementById("billing_address_state")?.closest(".col-xs-12");
    if (!stateWrapper) { return; }

    if (event.target.value === "US") {
      stateWrapper.classList.remove("hidden");
    } else {
      stateWrapper.classList.add("hidden");
    }
  }

  disableSubmit(form) {
    const button = form.querySelector(".add-new-billing-address");
    if (button) { button.disabled = true; }
  }

  enableSubmit(form) {
    const button = form.querySelector(".add-new-billing-address");
    if (button) { button.disabled = false; }
  }

  addOrReplaceHiddenInput(formId, formModel, newId) {
    if (!formId || !formModel) { return; }

    const existingInput = document.getElementById(`${formModel}_billing_address_id`);
    if (existingInput) {
      // jQuery's `.trigger("change")` deliberately: subscription_purchases_controller.js listens
      // for this through a jQuery-delegated handler on body ($("body").on("change...", "#subscription_billing_address_id", ...)).
      // jQuery's trigger() has no native invoker for "change" (P27/D7), so it never reaches
      // dispatchEvent - a native addEventListener anywhere in the ancestor chain would miss this.
      $(existingInput).val(newId).trigger("change");
    } else {
      const targetForm = document.getElementById(formId);
      if (!targetForm) { return; }

      // S2, fixed rather than accepted: this interpolated `formModel` and `newId` into a
      // single-quoted HTML string. `newId` is the new record's integer primary key (the same
      // controller actions above render `id: @billing_address.id`) and `formModel` is the declared
      // model value, so neither is attacker-controlled today -- but an apostrophe in either closes
      // the attribute, and D57 puts a response-derived value in a node rather than in markup.
      // type/name/id are reflected IDL attributes and `value` is set as an attribute, so the
      // element this builds is identical to the string it replaces.
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = `${formModel}[billing_address_id]`;
      input.id = `${formModel}_billing_address_id`;
      input.setAttribute("value", newId);
      targetForm.appendChild(input);

      // The same jQuery-delegated listener as the branch above, for the reason given there.
      $(input).trigger("change");
    }
  }

  paintErrors(errors) {
    Object.keys(errors).forEach((key) => {
      const input = document.getElementById(`billing_address_${key}`);
      if (!input) { return; }

      const formGroup = input.closest(".form-group");
      if (formGroup) { formGroup.classList.add("has-error"); }

      // `errors` is a serialised ActiveModel::Errors, so `errors[key]` is an array of validation
      // messages. Every validator on BillingAddress produces a static message today, but building
      // the node and setting textContent keeps that from mattering: a future validator that
      // interpolates the submitted value would otherwise make this a stored-XSS sink. Array
      // stringification is unchanged, so the rendered text is identical.
      const help = document.createElement("span");
      help.className = "help-block";
      help.textContent = errors[key];
      input.insertAdjacentElement("afterend", help);
    });
  }
}
